import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { radarDocenteAPI, adminAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { NIVELES, TIPOS_ESTABLECIMIENTO } from '../lib/utils';
import PerfilRubricaSelector from '../components/PerfilRubricaSelector';
import { 
    Loader2, Save, User, MapPin, School, Users, Search, Building2, 
    CheckCircle2, ExternalLink, RefreshCw, AlertCircle, ShieldCheck, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { trackFunnelEvent } from '../lib/funnel-tracker';

export default function Profile() {
    const { user, profile, updateProfile, refreshProfile, loading: authLoading } = useAuth();
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [showSearchDialog, setShowSearchDialog] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchRBD, setSearchRBD] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedEscuela, setSelectedEscuela] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    
    const [formData, setFormData] = useState({
        nombre: '',
        genero: '', // 'femenino' | 'masculino' | 'sin_especificar' | ''
        perfil: {
            nivel: '',
            asignatura: '',
            curso: '',
            tipo_establecimiento: '',
            dependencia: '',
            comuna: '',
            region: '',
            cantidad_estudiantes: 0,
            contexto_aula: '',
            perfil_rubrica: null,
            establecimiento_rbd: null,
            establecimiento_nombre: null,
            establecimiento_data: null,
        },
    });

    useEffect(() => {
        if (profile) {
            setFormData({
                nombre: profile.nombre || '',
                genero: profile.genero || '',
                perfil: {
                    nivel: profile.perfil?.nivel || '',
                    asignatura: profile.perfil?.asignatura || '',
                    curso: profile.perfil?.curso || '',
                    tipo_establecimiento: profile.perfil?.tipo_establecimiento || '',
                    dependencia: profile.perfil?.dependencia || '',
                    comuna: profile.perfil?.comuna || '',
                    region: profile.perfil?.region || '',
                    cantidad_estudiantes: profile.perfil?.cantidad_estudiantes || 0,
                    contexto_aula: profile.perfil?.contexto_aula || '',
                    perfil_rubrica: profile.perfil?.perfil_rubrica || null,
                    establecimiento_rbd: profile.perfil?.establecimiento_rbd || null,
                    establecimiento_nombre: profile.perfil?.establecimiento_nombre || null,
                    establecimiento_data: profile.perfil?.establecimiento_data || null,
                },
            });
        }
    }, [profile]);

    // Check if user is admin
    useEffect(() => {
        const checkAdmin = async () => {
            try {
                const response = await adminAPI.isAdmin();
                setIsAdmin(response.data.is_admin);
            } catch (error) {
                console.error('Error checking admin status:', error);
            }
        };
        checkAdmin();
    }, []);

    const handleChange = (field, value) => {
        if (field.startsWith('perfil.')) {
            const perfilField = field.replace('perfil.', '');
            setFormData((prev) => ({
                ...prev,
                perfil: {
                    ...prev.perfil,
                    [perfilField]: value,
                },
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [field]: value,
            }));
        }
    };

    const handleSearchEscuelas = async () => {
        setSearchLoading(true);
        trackFunnelEvent('school_search_started', {
            user_id: user?.id,
            last_action: searchRBD ? `rbd=${searchRBD}` : `query=${searchQuery || 'all'}`,
        });
        try {
            const params = { page_size: 50 };
            if (searchQuery) params.q = searchQuery;
            if (searchRBD) params.rbd = parseInt(searchRBD);

            const response = await radarDocenteAPI.searchEscuelas(params);
            setSearchResults(response.data.escuelas || []);

            // Backend degradado: Radar Docente no responde.
            if (response.data?.radar_disponible === false) {
                trackFunnelEvent('school_search_failed', {
                    user_id: user?.id,
                    error: 'radar_no_disponible',
                    last_action: searchRBD ? `rbd=${searchRBD}` : `query=${searchQuery || 'all'}`,
                });
                toast.warning(
                    response.data.mensaje ||
                        'No pudimos conectar con Radar Docente en este momento. Puedes continuar configurando tu perfil y vincular tu establecimiento más tarde.',
                    { duration: 8000 }
                );
                return;
            }

            const resultCount = response.data.escuelas?.length || 0;
            if (resultCount === 0) {
                trackFunnelEvent('school_search_empty', {
                    user_id: user?.id,
                    last_action: searchRBD ? `rbd=${searchRBD}` : `query=${searchQuery || 'all'}`,
                });
                if (searchRBD) {
                    toast.info(`RBD ${searchRBD} no encontrado en Radar Docente.`);
                } else if (searchQuery) {
                    toast.info(`No se encontraron establecimientos con "${searchQuery}"`);
                }
            } else {
                trackFunnelEvent('school_search_completed', {
                    user_id: user?.id,
                    last_action: `results=${resultCount}`,
                });
                const total = response.data.total || resultCount;
                if (total > 50) {
                    toast.info(`Mostrando 50 de ${total.toLocaleString()} resultados. Refina tu búsqueda.`);
                }
            }
        } catch (error) {
            console.error('Error searching escuelas:', error);
            trackFunnelEvent('school_search_failed', {
                user_id: user?.id,
                error: String(error?.response?.data?.detail || error?.message || 'unknown').slice(0, 200),
                last_action: `status=${error?.response?.status || 'network'}`,
            });
            toast.error(
                'No pudimos conectar con Radar Docente en este momento. Puedes continuar configurando tu perfil y vincular tu establecimiento más tarde.',
                { duration: 8000 }
            );
        } finally {
            setSearchLoading(false);
        }
    };

    const handleSelectEscuela = (escuela) => {
        setSelectedEscuela(escuela);
        trackFunnelEvent('school_selected', {
            user_id: user?.id,
            last_action: `rbd=${escuela.rbd}|${escuela.nombre?.slice(0, 80)}`,
        });
    };

    const handleSincronizar = async () => {
        if (!selectedEscuela) return;

        trackFunnelEvent('school_sync_attempted', {
            user_id: user?.id,
            last_action: `rbd=${selectedEscuela.rbd}|${selectedEscuela.nombre?.slice(0, 80)}`,
        });

        setSyncLoading(true);
        try {
            const response = await radarDocenteAPI.sincronizar(selectedEscuela.rbd);

            // Update local form data
            const updatedProfile = response.data.profile;
            setFormData({
                nombre: updatedProfile.nombre || formData.nombre,
                perfil: {
                    ...formData.perfil,
                    ...updatedProfile.perfil,
                },
            });

            // Refresh auth context
            await refreshProfile();

            setShowSearchDialog(false);
            setSearchQuery('');
            setSearchRBD('');
            setSearchResults([]);
            setSelectedEscuela(null);

            trackFunnelEvent('establecimiento_linked', {
                user_id: user?.id,
                last_action: `rbd=${selectedEscuela.rbd}|${selectedEscuela.nombre?.slice(0, 80)}`,
            });
            toast.success(`Establecimiento ${selectedEscuela.nombre} sincronizado correctamente`);
        } catch (error) {
            console.error('Error syncing escuela:', error);
            const status = error?.response?.status;
            const detail = error?.response?.data?.detail || error?.message || 'unknown';
            trackFunnelEvent('school_sync_failed', {
                user_id: user?.id,
                error: String(detail).slice(0, 200),
                last_action: `rbd=${selectedEscuela.rbd}|status=${status || 'network'}`,
            });
            const friendly = status === 404
                ? `El establecimiento (RBD ${selectedEscuela.rbd}) no fue encontrado en Radar Docente. Intenta refrescar o reportarlo por soporte.`
                : status === 503
                    ? 'Radar Docente no está respondiendo en este momento. Vuelve a intentarlo en unos minutos.'
                    : 'Error al sincronizar establecimiento';
            toast.error(friendly, { duration: 8000 });
        } finally {
            setSyncLoading(false);
        }
    };

    // Admin: Change establishment (preserves all previous work)
    const handleResetEstablecimiento = async () => {
        if (!isAdmin) return;
        
        if (!window.confirm(
            'Vas a desvincular tu establecimiento actual para vincular uno nuevo.\n\n' +
            'Tu trabajo anterior (borradores, conversaciones, análisis) se conservará intacto.\n\n' +
            '¿Deseas continuar?'
        )) return;
        
        setResetLoading(true);
        try {
            await adminAPI.resetEstablecimiento();
            await refreshProfile();
            
            // Clear local form data
            setFormData(prev => ({
                ...prev,
                perfil: {
                    ...prev.perfil,
                    establecimiento_rbd: null,
                    establecimiento_nombre: null,
                    establecimiento_data: null,
                    region: '',
                    comuna: '',
                    dependencia: '',
                    tipo_establecimiento: '',
                },
            }));
            
            toast.success('Establecimiento desvinculado. Ahora puedes buscar y vincular uno nuevo.');
        } catch (error) {
            console.error('Error resetting establishment:', error);
            toast.error('Error al cambiar establecimiento');
        } finally {
            setResetLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await updateProfile(formData);
            toast.success('Perfil actualizado correctamente');
        } catch (error) {
            console.error('Error updating profile:', error);
            toast.error('Error al actualizar el perfil');
        } finally {
            setLoading(false);
        }
    };

    const establecimientoData = formData.perfil.establecimiento_data;

    if (authLoading) {
        return (
            <Layout>
                <div className="flex h-96 items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="mx-auto max-w-4xl space-y-6 animate-fade-in">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-slate-900">Mi Perfil</h1>
                    <p className="mt-1 text-slate-500">
                        Completa tu información y vincula tu establecimiento desde Radar Docente
                    </p>
                </div>

                {/* Establecimiento Section - Priority */}
                <Card className="border-2 border-rose-200 bg-rose-50/30">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 font-heading text-lg text-rose-900">
                            <Building2 className="h-5 w-5" />
                            Establecimiento Educacional
                        </CardTitle>
                        <CardDescription className="text-rose-700">
                            Vincula tu establecimiento desde Radar Docente para contextualizar la orientación
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {establecimientoData ? (
                            <div className="space-y-4">
                                {/* Synced Establishment Info */}
                                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-start gap-3">
                                            <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
                                            <div>
                                                <h3 className="font-semibold text-emerald-900">
                                                    {establecimientoData.nombre}
                                                </h3>
                                                <p className="text-sm text-emerald-700">
                                                    RBD: {establecimientoData.rbd} | {establecimientoData.comuna}, {establecimientoData.region}
                                                </p>
                                            </div>
                                        </div>
                                        <Badge className="bg-emerald-100 text-emerald-700">Sincronizado</Badge>
                                    </div>
                                </div>

                                {/* Establishment Details Grid */}
                                <div className="space-y-4">
                                    {/* Datos Básicos */}
                                    <div>
                                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Datos Generales</h4>
                                        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                                            <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                                                <p className="text-xs text-slate-500">Dependencia</p>
                                                <p className="text-sm font-medium text-slate-900">{establecimientoData.dependencia || 'N/A'}</p>
                                            </div>
                                            <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                                                <p className="text-xs text-slate-500">Matrícula 2025</p>
                                                <p className="text-sm font-medium text-slate-900">{establecimientoData.matricula_2025?.toLocaleString() || 'N/A'}</p>
                                            </div>
                                            <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                                                <p className="text-xs text-slate-500">Docentes de Aula</p>
                                                <p className="text-sm font-medium text-slate-900">{establecimientoData.num_docentes_aula_2025 || 'N/A'}</p>
                                            </div>
                                            <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                                                <p className="text-xs text-slate-500">% Alumnos Prioritarios</p>
                                                <p className="text-sm font-medium text-slate-900">{establecimientoData.concentracion_alumnos_prioritarios?.toFixed(1) || 'N/A'}%</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Carrera Docente */}
                                    <div>
                                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Carrera Docente</h4>
                                        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                                            <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                                                <p className="text-xs text-slate-500">% Expertos I+II</p>
                                                <p className="text-sm font-medium text-indigo-700">{establecimientoData.pct_docentes_experto?.toFixed(1) || 'N/A'}%</p>
                                            </div>
                                            <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                                                <p className="text-xs text-slate-500">% Avanzados</p>
                                                <p className="text-sm font-medium text-emerald-700">{establecimientoData.pct_docentes_avanzado?.toFixed(1) || 'N/A'}%</p>
                                            </div>
                                            <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                                                <p className="text-xs text-slate-500">% Sin Carrera</p>
                                                <p className="text-sm font-medium text-amber-700">{establecimientoData.pct_sin_carrera_docente?.toFixed(1) || 'N/A'}%</p>
                                            </div>
                                            <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                                                <p className="text-xs text-slate-500">% Rotación (5 años)</p>
                                                <p className="text-sm font-medium text-rose-700">{establecimientoData.pct_rotacion_docente?.toFixed(1) || 'N/A'}%</p>
                                            </div>
                                            <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                                                <p className="text-xs text-slate-500">Riesgo Cambio</p>
                                                <p className="text-sm font-medium text-slate-900">{establecimientoData.riesgo_cambio_docente || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Estabilidad Directiva */}
                                    <div>
                                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Estabilidad Directiva</h4>
                                        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                            <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                                                <p className="text-xs text-slate-500">Cambio Directores (5 años)</p>
                                                <p className="text-sm font-medium text-slate-900">{establecimientoData.cambio_directores_5_anos || 'N/A'}</p>
                                            </div>
                                            <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                                                <p className="text-xs text-slate-500">Evaluación SNED</p>
                                                <p className="text-sm font-medium text-slate-900">{establecimientoData.evaluacion_sned || 'No evaluado'}</p>
                                            </div>
                                            <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                                                <p className="text-xs text-slate-500">Riesgo PEI</p>
                                                <p className="text-sm font-medium text-slate-900">{establecimientoData.riesgo_pei_combinado || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Ambiente de Aprendizaje */}
                                    <div>
                                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Ambiente de Aprendizaje</h4>
                                        <div className="grid gap-2 sm:grid-cols-2">
                                            <div className="rounded-lg border border-slate-200 bg-white p-3">
                                                <p className="text-xs font-semibold text-slate-600 mb-2">BÁSICA</p>
                                                <div className="space-y-1.5">
                                                    <div className="flex justify-between">
                                                        <span className="text-xs text-slate-500">Clasificación:</span>
                                                        <span className="text-xs font-medium text-slate-900">{establecimientoData.clasificacion_ambiente_basica || 'N/A'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-xs text-slate-500">% Buen ambiente:</span>
                                                        <span className="text-xs font-medium text-emerald-700">{establecimientoData.pct_buen_ambiente_basica ? (establecimientoData.pct_buen_ambiente_basica * 100).toFixed(1) + '%' : 'N/A'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-xs text-slate-500">% Mal ambiente:</span>
                                                        <span className="text-xs font-medium text-rose-700">{establecimientoData.pct_mal_ambiente_basica ? (establecimientoData.pct_mal_ambiente_basica * 100).toFixed(1) + '%' : 'N/A'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="rounded-lg border border-slate-200 bg-white p-3">
                                                <p className="text-xs font-semibold text-slate-600 mb-2">MEDIA</p>
                                                <div className="space-y-1.5">
                                                    <div className="flex justify-between">
                                                        <span className="text-xs text-slate-500">Clasificación:</span>
                                                        <span className="text-xs font-medium text-slate-900">{establecimientoData.clasificacion_ambiente_media || 'N/A'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-xs text-slate-500">% Buen ambiente:</span>
                                                        <span className="text-xs font-medium text-emerald-700">{establecimientoData.pct_buen_ambiente_media ? (establecimientoData.pct_buen_ambiente_media * 100).toFixed(1) + '%' : 'N/A'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-xs text-slate-500">% Mal ambiente:</span>
                                                        <span className="text-xs font-medium text-rose-700">{establecimientoData.pct_mal_ambiente_media ? (establecimientoData.pct_mal_ambiente_media * 100).toFixed(1) + '%' : 'N/A'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tendencia de Aprendizajes */}
                                    <div>
                                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Tendencia de Aprendizajes</h4>
                                        <div className="grid gap-2 sm:grid-cols-2">
                                            <div className="rounded-lg border border-slate-200 bg-white p-3">
                                                <p className="text-xs font-semibold text-slate-600 mb-2">BÁSICA</p>
                                                <div className="space-y-1.5">
                                                    <div className="flex justify-between">
                                                        <span className="text-xs text-slate-500">4° Lenguaje:</span>
                                                        <span className="text-xs font-medium text-slate-900">{establecimientoData.tendencia_basica_4b_lenguaje || 'N/A'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-xs text-slate-500">4° Matemáticas:</span>
                                                        <span className="text-xs font-medium text-slate-900">{establecimientoData.tendencia_basica_4b_matematicas || 'N/A'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-xs text-slate-500">6° Lenguaje:</span>
                                                        <span className="text-xs font-medium text-slate-900">{establecimientoData.tendencia_basica_6b_lenguaje || 'N/A'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-xs text-slate-500">6° Matemáticas:</span>
                                                        <span className="text-xs font-medium text-slate-900">{establecimientoData.tendencia_basica_6b_matematicas || 'N/A'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="rounded-lg border border-slate-200 bg-white p-3">
                                                <p className="text-xs font-semibold text-slate-600 mb-2">MEDIA</p>
                                                <div className="space-y-1.5">
                                                    <div className="flex justify-between">
                                                        <span className="text-xs text-slate-500">2° Lenguaje:</span>
                                                        <span className="text-xs font-medium text-slate-900">{establecimientoData.tendencia_media_2m_lenguaje || 'N/A'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-xs text-slate-500">2° Matemáticas:</span>
                                                        <span className="text-xs font-medium text-slate-900">{establecimientoData.tendencia_media_2m_matematicas || 'N/A'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-xs text-slate-500">Clasificación global:</span>
                                                        <span className="text-xs font-medium text-slate-900">{establecimientoData.clasificacion_global_media || 'N/A'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {isAdmin && (
                                        <Button 
                                            variant="outline" 
                                            className="gap-2 border-slate-300 hover:bg-slate-100"
                                            onClick={handleResetEstablecimiento}
                                            disabled={resetLoading}
                                            data-testid="admin-reset-establecimiento-btn"
                                        >
                                            {resetLoading ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <RotateCcw className="h-4 w-4" />
                                            )}
                                            Cambiar Establecimiento
                                        </Button>
                                    )}
                                    <a
                                        href={process.env.REACT_APP_RADAR_DOCENTE_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <Button variant="ghost" className="gap-2">
                                            <ExternalLink className="h-4 w-4" />
                                            Ver en Radar Docente
                                        </Button>
                                    </a>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                                    <AlertCircle className="h-5 w-5 text-amber-600" />
                                    <div>
                                        <p className="font-medium text-amber-800">Establecimiento no vinculado</p>
                                        <p className="text-sm text-amber-700">
                                            Vincula tu establecimiento para recibir orientación contextualizada
                                        </p>
                                    </div>
                                </div>
                                
                                <Dialog open={showSearchDialog} onOpenChange={(open) => {
                                    setShowSearchDialog(open);
                                    if (open) {
                                        trackFunnelEvent('school_search_opened', {
                                            user_id: user?.id,
                                            last_action: 'dialog_opened',
                                        });
                                    }
                                }}>
                                    <DialogTrigger asChild>
                                        <Button className="gap-2" data-testid="search-escuela-btn">
                                            <Search className="h-4 w-4" />
                                            Buscar mi Establecimiento
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent className="max-w-2xl">
                                        <DialogHeader>
                                            <DialogTitle>Buscar Establecimiento en Radar Docente</DialogTitle>
                                            <DialogDescription>
                                                Busca por nombre o RBD para vincular tu establecimiento
                                            </DialogDescription>
                                        </DialogHeader>
                                        <SearchDialogContent 
                                            searchQuery={searchQuery}
                                            setSearchQuery={setSearchQuery}
                                            searchRBD={searchRBD}
                                            setSearchRBD={setSearchRBD}
                                            searchResults={searchResults}
                                            selectedEscuela={selectedEscuela}
                                            searchLoading={searchLoading}
                                            syncLoading={syncLoading}
                                            onSearch={handleSearchEscuelas}
                                            onSelect={handleSelectEscuela}
                                            onSync={handleSincronizar}
                                        />
                                    </DialogContent>
                                </Dialog>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Personal Info */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 font-heading text-lg">
                                <User className="h-5 w-5" />
                                Información Personal
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <Label htmlFor="nombre">Nombre Completo</Label>
                                    <Input
                                        id="nombre"
                                        value={formData.nombre}
                                        onChange={(e) => handleChange('nombre', e.target.value)}
                                        placeholder="María González"
                                        data-testid="profile-nombre"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email">Correo Electrónico</Label>
                                    <Input
                                        id="email"
                                        value={user?.email || ''}
                                        disabled
                                        className="bg-slate-50"
                                    />
                                </div>
                            </div>

                            {/* Género — para personalizar saludos en correos institucionales */}
                            <div className="space-y-2 pt-2" data-testid="profile-genero-block">
                                <Label className="text-sm">Forma de tratamiento</Label>
                                <p className="text-xs text-slate-500 -mt-1">
                                    Lo usaremos solo para personalizar el saludo en los correos
                                    (ej. "Estimada Tamara" o "Estimado Julio").
                                </p>
                                <div className="flex flex-wrap gap-3 pt-1">
                                    {[
                                        { value: 'femenino', label: 'Profesora' },
                                        { value: 'masculino', label: 'Profesor' },
                                        { value: 'sin_especificar', label: 'Prefiero no decir' },
                                    ].map((opt) => (
                                        <label
                                            key={opt.value}
                                            className={
                                                'flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors text-sm ' +
                                                (formData.genero === opt.value
                                                    ? 'border-slate-900 bg-slate-50 text-slate-900 font-medium'
                                                    : 'border-slate-200 hover:border-slate-300 text-slate-700')
                                            }
                                            data-testid={`profile-genero-${opt.value}`}
                                        >
                                            <input
                                                type="radio"
                                                name="genero"
                                                value={opt.value}
                                                checked={formData.genero === opt.value}
                                                onChange={(e) => handleChange('genero', e.target.value)}
                                                className="accent-slate-900"
                                            />
                                            {opt.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Teaching Info */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 font-heading text-lg">
                                <School className="h-5 w-5" />
                                Información Docente
                            </CardTitle>
                            <CardDescription>
                                Esta información ayuda a contextualizar la orientación
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <Label>Nivel de Enseñanza</Label>
                                    <Select
                                        value={formData.perfil.nivel}
                                        onValueChange={(value) => handleChange('perfil.nivel', value)}
                                    >
                                        <SelectTrigger data-testid="profile-nivel">
                                            <SelectValue placeholder="Selecciona un nivel" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {NIVELES.map((nivel) => (
                                                <SelectItem key={nivel} value={nivel}>
                                                    {nivel}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="asignatura">Asignatura</Label>
                                    <Input
                                        id="asignatura"
                                        value={formData.perfil.asignatura}
                                        onChange={(e) => handleChange('perfil.asignatura', e.target.value)}
                                        placeholder="Ej: Matemáticas, Lenguaje"
                                        data-testid="profile-asignatura"
                                    />
                                </div>
                            </div>
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <Label htmlFor="curso">Curso</Label>
                                    <Input
                                        id="curso"
                                        value={formData.perfil.curso}
                                        onChange={(e) => handleChange('perfil.curso', e.target.value)}
                                        placeholder="Ej: 4° Básico A"
                                        data-testid="profile-curso"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="cantidad_estudiantes">Cantidad de Estudiantes</Label>
                                    <Input
                                        id="cantidad_estudiantes"
                                        type="number"
                                        min="0"
                                        max="50"
                                        value={formData.perfil.cantidad_estudiantes}
                                        onChange={(e) => handleChange('perfil.cantidad_estudiantes', parseInt(e.target.value) || 0)}
                                        data-testid="profile-cantidad-estudiantes"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Perfil de Rúbrica (Módulo 2 - Clase Grabada) */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 font-heading text-lg">
                                <School className="h-5 w-5" />
                                Perfil de Rúbrica · Módulo 2
                            </CardTitle>
                            <CardDescription>
                                Define cómo la IA debe conversar contigo sobre tu clase grabada
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <PerfilRubricaSelector
                                value={formData.perfil.perfil_rubrica}
                                onChange={(value) => handleChange('perfil.perfil_rubrica', value)}
                                nivel={formData.perfil.nivel}
                            />
                        </CardContent>
                    </Card>

                    {/* Classroom Context */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 font-heading text-lg">
                                <Users className="h-5 w-5" />
                                Contexto del Aula
                            </CardTitle>
                            <CardDescription>
                                Describe las características de tu curso para orientación más precisa
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="contexto_aula">Contexto del Curso</Label>
                                <Textarea
                                    id="contexto_aula"
                                    value={formData.perfil.contexto_aula}
                                    onChange={(e) => handleChange('perfil.contexto_aula', e.target.value)}
                                    placeholder="Describe características relevantes: PIE, alta vulnerabilidad, ruralidad, estudiantes migrantes, clima de aula, dificultades observadas, etc."
                                    rows={4}
                                    data-testid="profile-contexto"
                                />
                                <p className="text-xs text-slate-500">
                                    Esta información es confidencial y solo se usa para personalizar la orientación.
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Submit Button */}
                    <div className="flex justify-end">
                        <Button type="submit" className="gap-2" disabled={loading} data-testid="save-profile-btn">
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4" />
                                    Guardar Perfil
                                </>
                            )}
                        </Button>
                    </div>
                </form>

                {/* Reset zone — destructive, requires explicit confirmation */}
                <ResetWorkSection />
            </div>
        </Layout>
    );
}

// Search Dialog Content Component
function ResetWorkSection() {
    const [open, setOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [loading, setLoading] = useState(false);
    const handleReset = async () => {
        setLoading(true);
        try {
            const { accountAPI } = await import('../lib/api');
            await accountAPI.reset();
            toast.success('Tu trabajo fue reiniciado. Tu cuenta queda intacta.');
            setOpen(false);
            setConfirmText('');
            // Recargar la app para reiniciar el estado del UsageContext/Auth
            setTimeout(() => { window.location.href = '/dashboard'; }, 600);
        } catch (err) {
            toast.error('No se pudo reiniciar el trabajo. Vuelve a intentar.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mt-12 rounded-2xl border border-rose-200 bg-rose-50/60 p-6" data-testid="reset-work-section">
            <h3 className="font-heading text-base font-bold text-rose-900">Reiniciar tu trabajo</h3>
            <p className="mt-2 text-sm text-rose-900/80 leading-relaxed">
                Esta acción elimina <strong>todos tus borradores, conversaciones de Chat IA y tu progreso</strong>.
                Tu cuenta y tu perfil docente (establecimiento, estrategia, perfil de rúbrica) se mantienen intactos.
                Si quieres borrar la cuenta completa, escríbenos desde{' '}
                <button
                    type="button"
                    onClick={() =>
                        window.dispatchEvent(
                            new CustomEvent('open-soporte', {
                                detail: { asunto: 'Borrar cuenta completa', mensaje: '' },
                            })
                        )
                    }
                    className="underline text-rose-700 hover:text-rose-900"
                    data-testid="profile-soporte-borrar-cuenta"
                >
                    el formulario de soporte
                </button>
                .
            </p>
            {!open ? (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="mt-4 inline-flex items-center gap-2 rounded-lg border border-rose-300 bg-white px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50"
                    data-testid="reset-work-open"
                >
                    Resetear trabajo
                </button>
            ) : (
                <div className="mt-4 rounded-xl border border-rose-200 bg-white p-4" data-testid="reset-work-modal">
                    <p className="text-sm text-slate-900 font-medium">
                        Si reseteas tu trabajo, toda la información guardada en este portal será eliminada definitivamente y no podrá recuperarse. ¿Estás seguro de continuar?
                    </p>
                    <p className="mt-3 text-xs text-slate-600">
                        Para confirmar, escribe <span className="font-mono font-bold">RESETEAR</span> en el campo de abajo:
                    </p>
                    <input
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="Escribe RESETEAR"
                        className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                        data-testid="reset-work-confirm-input"
                    />
                    <div className="mt-4 flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={handleReset}
                            disabled={loading || confirmText !== 'RESETEAR'}
                            className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40 hover:bg-rose-700"
                            data-testid="reset-work-confirm"
                        >
                            {loading ? 'Reseteando…' : 'Sí, resetear completamente'}
                        </button>
                        <button
                            type="button"
                            onClick={() => { setOpen(false); setConfirmText(''); }}
                            disabled={loading}
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                            data-testid="reset-work-cancel"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}


// Search Dialog Content Component
function SearchDialogContent({
    searchQuery,
    setSearchQuery,
    searchRBD,
    setSearchRBD,
    searchResults,
    selectedEscuela,
    searchLoading,
    syncLoading,
    onSearch,
    onSelect,
    onSync,
}) {
    const [showAll, setShowAll] = React.useState(false);

    // Load all schools on first render
    React.useEffect(() => {
        if (searchResults.length === 0 && !searchLoading) {
            setShowAll(true);
            onSearch(); // Load all schools
        }
    }, []);

    return (
        <div className="space-y-4 pt-4">
            {/* Info banner */}
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
                <p className="text-sm text-blue-800">
                    <strong>Radar Docente</strong> cuenta con más de <strong>6,800 establecimientos</strong> de Chile. 
                    Busca por nombre o ingresa el RBD de tu establecimiento.
                </p>
            </div>

            <div className="flex gap-2">
                <div className="flex-1">
                    <Input
                        placeholder="Buscar por nombre..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && onSearch()}
                        data-testid="search-escuela-input"
                    />
                </div>
                <div className="w-32">
                    <Input
                        placeholder="RBD"
                        value={searchRBD}
                        onChange={(e) => setSearchRBD(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && onSearch()}
                        data-testid="search-rbd-input"
                    />
                </div>
                <Button onClick={onSearch} disabled={searchLoading} data-testid="search-btn">
                    {searchLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Search className="h-4 w-4" />
                    )}
                </Button>
            </div>

            {/* Show all button */}
            {searchResults.length === 0 && !searchLoading && (
                <Button 
                    variant="outline" 
                    className="w-full" 
                    onClick={() => {
                        setSearchQuery('');
                        setSearchRBD('');
                        onSearch();
                    }}
                    data-testid="show-all-btn"
                >
                    Ver todos los establecimientos disponibles
                </Button>
            )}

            {searchResults.length > 0 && (
                <div>
                    <p className="text-xs text-slate-500 mb-2">
                        Mostrando {searchResults.length} establecimiento(s)
                    </p>
                    <ScrollArea className="h-64 rounded-lg border">
                    <div className="p-2 space-y-1">
                        {searchResults.map((escuela) => (
                            <div
                                key={escuela.rbd}
                                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                                    selectedEscuela?.rbd === escuela.rbd
                                        ? 'bg-slate-100 border-2 border-slate-400'
                                        : 'hover:bg-slate-50 border border-transparent'
                                }`}
                                onClick={() => onSelect(escuela)}
                                data-testid={`escuela-item-${escuela.rbd}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-slate-900 truncate">{escuela.nombre}</p>
                                    <p className="text-sm text-slate-500">
                                        RBD: {escuela.rbd} | {escuela.comuna}, {escuela.region}
                                    </p>
                                    <p className="text-xs text-slate-400 truncate">{escuela.dependencia}</p>
                                </div>
                                {selectedEscuela?.rbd === escuela.rbd && (
                                    <CheckCircle2 className="h-5 w-5 text-slate-600 flex-shrink-0 ml-2" />
                                )}
                            </div>
                        ))}
                    </div>
                </ScrollArea>
                </div>
            )}

            {selectedEscuela && (
                <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <div>
                        <p className="font-medium text-slate-900">Seleccionado: {selectedEscuela.nombre}</p>
                        <p className="text-sm text-slate-500">RBD: {selectedEscuela.rbd}</p>
                    </div>
                    <Button onClick={onSync} disabled={syncLoading} data-testid="sync-escuela-btn">
                        {syncLoading ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Sincronizando...
                            </>
                        ) : (
                            <>
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                Vincular
                            </>
                        )}
                    </Button>
                </div>
            )}

            <p className="text-xs text-slate-500 text-center">
                Datos proporcionados por{' '}
                <a 
                    href={process.env.REACT_APP_RADAR_DOCENTE_URL}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-slate-700 underline"
                >
                    Radar Docente
                </a>
            </p>
        </div>
    );
}
